import React, { useState, useEffect } from 'react';
import { Bell, BellOff, Check, X } from 'lucide-react';
import { 
  requestNotificationPermission, 
  getNotificationPermission, 
  areNotificationsSupported,
  disableNotifications,
  onForegroundMessage
} from '../lib/notifications';

/**
 * Composant pour demander la permission des notifications push
 * Affiche un banner élégant en haut de l'app
 */
const NotificationPrompt = ({ user, activeCompanyId, onPermissionGranted }) => {
  const [permission, setPermission] = useState('default');
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!areNotificationsSupported()) return;
    
    const currentPermission = getNotificationPermission();
    setPermission(currentPermission);
    
    // Afficher le prompt seulement si la permission n'a pas été donnée
    // et que l'utilisateur n'a pas explicitement refusé
    const hasDeclined = localStorage.getItem('notifications_declined');
    setIsVisible(currentPermission === 'default' && !hasDeclined);
  }, []);

  const handleEnable = async () => {
    if (!user || !activeCompanyId) return;
    
    setIsLoading(true);
    const granted = await requestNotificationPermission(user.uid, activeCompanyId);
    
    if (granted) {
      setPermission('granted');
      setIsVisible(false);
      if (onPermissionGranted) onPermissionGranted();
    } else {
      setPermission(Notification.permission);
    }
    
    setIsLoading(false);
  };

  const handleDecline = () => {
    localStorage.setItem('notifications_declined', 'true');
    setIsVisible(false);
  };

  if (!isVisible || !areNotificationsSupported()) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg animate-slide-down">
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1">
          <Bell className="w-5 h-5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium">
              Activez les notifications pour rester informé en temps réel
            </p>
            <p className="text-xs text-blue-100 mt-0.5">
              Nouveaux devis, signatures, messages... ne ratez plus rien !
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={handleEnable}
            disabled={isLoading}
            className="bg-white text-blue-600 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-50 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                Activation...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Activer
              </>
            )}
          </button>
          
          <button
            onClick={handleDecline}
            className="text-white hover:text-blue-100 p-2 rounded-lg hover:bg-blue-600 transition"
            title="Plus tard"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Composant de paramètres des notifications
 * À intégrer dans la page Settings
 */
export const NotificationSettings = ({ user, activeCompanyId }) => {
  const [permission, setPermission] = useState('default');
  const [isLoading, setIsLoading] = useState(false);
  const [preferences, setPreferences] = useState({
    documentSigned: true,
    newDocument: true,
    siteCreated: true,
    eventCreated: true,
    eventReminder: true,
    invoiceDue: true,
    teamMessages: true
  });

  useEffect(() => {
    if (areNotificationsSupported()) {
      setPermission(getNotificationPermission());
    }
  }, []);

  const handleEnableNotifications = async () => {
    setIsLoading(true);
    const granted = await requestNotificationPermission(user.uid, activeCompanyId);
    setPermission(granted ? 'granted' : Notification.permission);
    setIsLoading(false);
  };

  const handleDisableNotifications = async () => {
    setIsLoading(true);
    await disableNotifications(user.uid);
    setPermission('denied');
    setIsLoading(false);
  };

  const togglePreference = (key) => {
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
    // TODO: Sauvegarder dans Firestore
  };

  if (!areNotificationsSupported()) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-sm text-yellow-800">
          ⚠️ Les notifications push ne sont pas disponibles sur cet appareil ou navigateur.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            {permission === 'granted' ? (
              <Bell className="w-6 h-6 text-green-600" />
            ) : (
              <BellOff className="w-6 h-6 text-slate-400" />
            )}
            <div>
              <h3 className="font-semibold text-slate-900">Notifications push</h3>
              <p className="text-sm text-slate-600">
                {permission === 'granted' && 'Les notifications sont activées'}
                {permission === 'denied' && 'Les notifications sont désactivées'}
                {permission === 'default' && 'Les notifications ne sont pas configurées'}
              </p>
            </div>
          </div>

          {permission !== 'granted' && (
            <button
              onClick={handleEnableNotifications}
              disabled={isLoading}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 transition disabled:opacity-50"
            >
              {isLoading ? 'Activation...' : 'Activer'}
            </button>
          )}

          {permission === 'granted' && (
            <button
              onClick={handleDisableNotifications}
              disabled={isLoading}
              className="bg-red-50 text-red-600 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-red-100 transition disabled:opacity-50"
            >
              Désactiver
            </button>
          )}
        </div>

        {permission === 'granted' && (
          <div className="mt-6 pt-6 border-t border-slate-200 space-y-3">
            <h4 className="text-sm font-semibold text-slate-900 mb-3">Types de notifications</h4>
            
            {[
              { key: 'documentSigned', label: '✍️ Documents signés', desc: 'Quand un client signe un devis ou une facture' },
              { key: 'newDocument', label: '📄 Nouveaux documents', desc: 'Création de devis, factures, chantiers' },
              { key: 'siteCreated', label: '🏗️ Nouveaux chantiers', desc: 'Quand un nouveau chantier est créé' },
              { key: 'eventCreated', label: '📅 Nouveaux rendez-vous', desc: 'Quand un rendez-vous est ajouté à l\'agenda' },
              { key: 'eventReminder', label: '⏰ Rappels de rendez-vous', desc: 'Rappels avant les rendez-vous (24h avant)' },
              { key: 'invoiceDue', label: '💰 Factures en retard', desc: 'Rappels pour les paiements en retard' },
              { key: 'teamMessages', label: '💬 Messages d\'équipe', desc: 'Notifications des membres de l\'équipe' }
            ].map(({ key, label, desc }) => (
              <label key={key} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition">
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-900">{label}</p>
                  <p className="text-xs text-slate-600 mt-0.5">{desc}</p>
                </div>
                <input
                  type="checkbox"
                  checked={preferences[key]}
                  onChange={() => togglePreference(key)}
                  className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
              </label>
            ))}
          </div>
        )}
      </div>

      {permission === 'denied' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-800 mb-2">
            <strong>Les notifications sont bloquées par votre navigateur.</strong>
          </p>
          <p className="text-xs text-red-700">
            Pour les réactiver, cliquez sur l'icône de cadenas dans la barre d'adresse, puis autorisez les notifications pour ce site.
          </p>
        </div>
      )}
    </div>
  );
};

export default NotificationPrompt;
